// src/hooks/useFitText.js
import { useEffect } from "react";

/**
 * Auto-fits text to a maximum number of lines by decreasing font-size when needed.
 * - For single-line, it ensures no wrap by shrinking until scrollWidth <= clientWidth
 * - For multi-line, it shrinks until scrollHeight fits within maxLines * lineHeight
 *
 * @param {React.RefObject<HTMLElement>} elementRef
 * @param {{ maxLines: 1|2|3, minFontSizePx?: number, stepPx?: number, debounceMs?: number }} options
 */
export default function useFitText(elementRef, options) {
  const { maxLines = 1, minFontSizePx = 12, stepPx = 0.5, debounceMs = 50, preferWrapOnWide = false } = options || {};

  useEffect(() => {
    if (!elementRef?.current) return;

    let frame = null;
    let timeout = null;

    const el = elementRef.current;

    const parsePx = (value) => {
      const n = parseFloat(String(value || "").replace("px", ""));
      return Number.isFinite(n) ? n : 0;
    };

    const getLineHeightPx = (computed, fontSizePx) => {
      const lh = computed.lineHeight;
      if (!lh || lh === "normal") return fontSizePx * 1.2; // reasonable fallback
      return parsePx(lh);
    };

    const fit = () => {
      // Measure current styles
      const computed = window.getComputedStyle(el);
      let currentFont = parsePx(computed.fontSize);
      if (!currentFont) currentFont = 16;

      // Keep a reference to the initial font size in case future enhancements need it.
      const ORIGINAL_FONT = currentFont;

      const fitsOneLine = () => el.scrollWidth <= el.clientWidth + 0.5; // tolerance

      const fitsMaxLines = () => {
        const fontSizePx = parsePx(window.getComputedStyle(el).fontSize);
        const lineHeightPx = getLineHeightPx(window.getComputedStyle(el), fontSizePx);
        const maxHeight = lineHeightPx * maxLines + 0.5; // tolerance
        return el.scrollHeight <= maxHeight;
      };
      const fitsWidth = () => el.scrollWidth <= el.clientWidth + 0.5;
      const fitsBoth = () => fitsMaxLines() && fitsWidth();

      // Reset to current (or original) before trying
      el.style.fontSize = `${currentFont}px`;

      if (maxLines === 1) {
        // Always enforce nowrap for single line
        const prevWhiteSpace = el.style.whiteSpace;
        el.style.whiteSpace = "nowrap";
        
        // Decrease until it fits or min reached
        while (!fitsOneLine() && currentFont > minFontSizePx) {
          currentFont = Math.max(minFontSizePx, currentFont - stepPx);
          el.style.fontSize = `${currentFont}px`;
        }
        
        // restore inline whiteSpace
        el.style.whiteSpace = prevWhiteSpace || "nowrap";
      } else {
        // For multi-line (maxLines > 1), check if we're on mobile
        const isNarrow = window.innerWidth <= 768; // 改为更合理的窄屏阈值
        const prevWhiteSpace = el.style.whiteSpace;
        
        if (isNarrow) {
          // 窄屏模式：优先考虑可读性，允许换行
          el.style.whiteSpace = "normal";
          
          // 在窄屏下，先尝试使用较大的字体，如果超出最大行数再缩小
          let testFont = Math.max(currentFont, minFontSizePx + 4); // 从稍大的字体开始
          el.style.fontSize = `${testFont}px`;
          
          // 如果超出最大行数，逐步缩小
          while (!fitsMaxLines() && testFont > minFontSizePx) {
            testFont = Math.max(minFontSizePx, testFont - stepPx);
            el.style.fontSize = `${testFont}px`;
          }
          
          currentFont = testFont;
        } else {
          // 宽屏模式：可选优先换行以获得更大字号
          const prevDisplay = el.style.display;
          if (preferWrapOnWide) {
            el.style.whiteSpace = "normal";
            el.style.display = "block";
            let guard = 0;
            while (!fitsBoth() && currentFont > minFontSizePx && guard < 200) {
              currentFont = Math.max(minFontSizePx, currentFont - stepPx);
              el.style.fontSize = `${currentFont}px`;
              guard += 1;
            }
            el.style.display = prevDisplay || "";
          } else {
            // 默认策略：先尝试单行，再按最大行数收缩
            el.style.whiteSpace = "nowrap";
            while (!fitsOneLine() && currentFont > minFontSizePx) {
              currentFont = Math.max(minFontSizePx, currentFont - stepPx);
              el.style.fontSize = `${currentFont}px`;
            }
            if (!fitsOneLine()) {
              el.style.whiteSpace = "normal";
            }
            el.style.display = "block";
            let guard = 0;
            while (!fitsBoth() && currentFont > minFontSizePx && guard < 200) {
              currentFont = Math.max(minFontSizePx, currentFont - stepPx);
              el.style.fontSize = `${currentFont}px`;
              guard += 1;
            }
            el.style.display = prevDisplay || "";
          }
        }
        
        // restore whiteSpace
        el.style.whiteSpace = prevWhiteSpace || "";
      }
    };

    const schedule = () => {
      if (frame) cancelAnimationFrame(frame);
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        frame = requestAnimationFrame(fit);
      }, debounceMs);
    };

    // Observe size changes (feature-detect ResizeObserver)
    const supportsResizeObserver = typeof window !== 'undefined' && 'ResizeObserver' in window;
    let resizeObserver = null;
    if (supportsResizeObserver) {
      resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(el);
    } else {
      window.addEventListener("resize", schedule);
    }

    // Initial
    schedule();

    return () => {
      if (frame) cancelAnimationFrame(frame);
      if (timeout) clearTimeout(timeout);
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", schedule);
      }
    };
  }, [elementRef, maxLines, minFontSizePx, stepPx, debounceMs, preferWrapOnWide]);
}


